import type { CreateSiteRequest, CreateSiteResponse } from '@markbird/shared';
import { db } from './db';
import { getIdToken } from './auth';
import { rememberLocalShare } from './localShares';

/**
 * Publishing a folder of notes as one small site.
 *
 * The selection is a folder and everything under it, because that is a
 * grouping people already maintain: a handbook, a project, a wiki. The pages
 * go in the order the library shows them, so the site reads the way the folder
 * reads.
 *
 * Nothing about the library is uploaded beyond the notes themselves — no ids,
 * no tags, no history. A site is a copy that was published, not a window into
 * the library.
 */

export interface SitePlan {
  folder: string;
  pages: { title: string; content: string }[];
}

/** The notes of a folder and its subfolders, in library order. */
export async function planSite(folder: string): Promise<SitePlan> {
  const prefix = `${folder}/`;
  const docs = (await db.documents.toArray())
    .filter((doc) => doc.deletedAt == null)
    .filter((doc) => doc.folder === folder || (doc.folder ?? '').startsWith(prefix))
    .filter((doc) => doc.content.trim() !== '')
    .sort(
      (a, b) => (a.folder ?? '').localeCompare(b.folder ?? '') || a.title.localeCompare(b.title),
    );
  return {
    folder,
    pages: docs.map((doc) => ({ title: doc.title || 'Untitled', content: doc.content })),
  };
}

export type PublishResult =
  | { ok: true; site: CreateSiteResponse }
  | { ok: false; error: 'slug_taken' | 'too_large' | 'rate_limited' | 'failed'; message?: string };

/** Publish, and remember the secret that takes it down again. */
export async function publishSite(
  request: CreateSiteRequest,
  title: string,
): Promise<PublishResult> {
  try {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const token = await getIdToken();
    if (token) headers.authorization = `Bearer ${token}`;

    const res = await fetch('/api/sites', {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      const known = ['slug_taken', 'too_large', 'rate_limited'] as const;
      const error = known.find((code) => code === body.error) ?? 'failed';
      return { ok: false, error, message: body.message };
    }
    const site = (await res.json()) as CreateSiteResponse;
    // Remembered like a share link, because that is what it needs to be: a
    // thing this device can take back without an account.
    await rememberLocalShare({
      token: site.id,
      kind: 'site',
      url: site.url,
      title,
      createdAt: Date.now(),
      expiresAt: site.expiresAt,
      deleteToken: site.deleteToken,
      deleteUrl: site.deleteUrl,
    });
    return { ok: true, site };
  } catch {
    return { ok: false, error: 'failed' };
  }
}
